/// <amd-module name="Wit/WorkItemForm/Links/Controls/ArtifactLinkIcon" />
import * as React from "react";
import { useEffect, useState } from "react";
import { usePageContext } from "VSS/Platform/Layout";
import { IIdentity, IVssIdentityService, VssIdentityServiceName } from "VSS/Features/Identities/IdentityService";
import { IBasicIdentityRef, Persona, PersonaSize } from "VSSUI/Persona";
import { Icon, IconSize, IIconProps } from "VSSUI/Icon";
import { TypeIconSize, WorkItemTypeIcon, WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { getIconDataObservable } from "Wit/UI/Services/WorkItemTypeIconMetadataService";


export function ArtifactLinkIcon({ iconDescriptor }: { iconDescriptor: unknown }) {
    const pageContext = usePageContext();
    if (hasIconName(iconDescriptor)) {
        return <Icon className="icon-margin" size={IconSize.medium} {...iconDescriptor} />;
    }

    if (isWorkItemType(iconDescriptor)) {
        const iconData = getIconDataObservable(iconDescriptor.project, iconDescriptor.workItemTypeName, pageContext);
        return <WorkItemTypeIcon className="icon-margin" size={TypeIconSize.normal} data={iconData} />;
    }

    if (isWorkItemTypeColorAndIcon(iconDescriptor)) {
        return <WorkItemTypeIcon className="icon-margin" size={TypeIconSize.normal} data={iconDescriptor} />;
    }

    if (isIdentity(iconDescriptor)) {
        return <AsyncPersona identity={iconDescriptor}/>
    }

    return null;
}

function AsyncPersona({ identity }: { identity: IBasicIdentityRef & {email?: string}}) {
    const pageContext = usePageContext();
    const [resolvedIdentity, setResolvedIdentity] = useState<IBasicIdentityRef | IIdentity>(identity);

    useEffect(() => {
        if (identity !== resolvedIdentity) {
            setResolvedIdentity(identity);
        }
        if (identity.imageUrl) {
            return;
        }

        const identityService = pageContext.getService<IVssIdentityService>(VssIdentityServiceName);
        identityService.searchIdentitiesAsync(identity.email ?? identity.displayName).then(matchingIdentities => {
            if (matchingIdentities.length === 1) {
                setResolvedIdentity(matchingIdentities[0]);
            }
        });

    }, [identity]);

    return <Persona className="icon-margin" size={PersonaSize.size16} identity={resolvedIdentity} />;
}

function hasIconName(value: unknown): value is IIconProps {
    if (!value) {
        return false;
    }
    const anyValue = value as any;
    return typeof anyValue.iconName === "string" || typeof anyValue.render === "function";
}

function isWorkItemType(value: unknown): value is { project: string; workItemTypeName: string } {
    if (!value) {
        return false;
    }
    const anyValue = value as any;
    return typeof anyValue.project === "string" && typeof anyValue.workItemTypeName === "string";
}

function isWorkItemTypeColorAndIcon(value: unknown): value is WorkItemTypeColorAndIcon {
    if (!value) {
        return false;
    }
    const anyValue = value as any;
    return typeof anyValue.color === "string" && typeof anyValue.icon == 'string' && typeof anyValue.workItemTypeName === 'string';
}

function isIdentity(value: unknown): value is IBasicIdentityRef {
    if (!value) {
        return false;
    }
    const anyValue = value as any;
    return typeof anyValue.displayName === "string" && typeof anyValue.id === "string";
}
